/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx';
import { useWorkspace } from 'hooks/useWorkspace';
import React, { useCallback, useEffect, useState, useTransition } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Link as WouterLink, useLocation, useRoute } from 'wouter';
import { useApiExplorerContext } from 'wrappers/ApiExplorerProvider';
import { z } from 'zod';
import { Menu } from '@headlessui/react';

import type { WorkspaceSettingsTabsType } from '../pages/SchemasV2/WorkspaceSettings/WorkspaceSettingsModal';
import { useUserInfo } from '../../hooks/useUserInfo';
import {
  WORKSPACE_SETTINGS_MODAL_URL_PREFIX,
  WorkspaceSettingsModal,
  WorkspaceSettingsTabs,
} from '../pages/SchemasV2/WorkspaceSettings/WorkspaceSettingsModal';
import { LogoutButton } from '../Profile/LogoutButton/LogoutButtonV2';

type Props = {
  addRoutingToHomePage?: boolean;
  className?: string;
  disabled?: boolean;
};

const TabRouteSchema = z.enum(WorkspaceSettingsTabs).nullish();

export const WorkspaceSwitcher = ({
  addRoutingToHomePage = false,
  className,
  disabled = false,
}: Props) => {
  const { setIsOpen } = useApiExplorerContext();
  const [pathMatch, params] = useRoute(
    `${WORKSPACE_SETTINGS_MODAL_URL_PREFIX}/:tab*`
  );
  const {
    workspace: currentWorkspace,
    buildNewWorkspaceUrl,
    workspaces,
  } = useWorkspace();
  const [, navigate] = useLocation();

  const workspaceId = currentWorkspace?.id;
  const { data: userInfo } = useUserInfo();

  const [openSettingsTab, setOpenSettingsTab] =
    useState<WorkspaceSettingsTabsType | null>(null);
  // We need the useTransition() here to prevent the user info render fetching from
  // triggering suspense.
  const [, startTransition] = useTransition();
  const handleClose = useCallback(
    () => startTransition(() => setOpenSettingsTab(null)),
    []
  );
  useHotkeys('esc', handleClose, {
    enabled: true,
  });

  useEffect(() => {
    if (pathMatch) {
      const parsedTabRoute = TabRouteSchema.safeParse(params.tab);
      const defaultSettingsTab = parsedTabRoute.success
        ? parsedTabRoute.data
        : undefined;
      // We received an invalid value in the slug.
      if (parsedTabRoute.error) {
        navigate(WORKSPACE_SETTINGS_MODAL_URL_PREFIX, { replace: true });
      }
      setOpenSettingsTab(defaultSettingsTab ?? 'workspace');
    }
  }, [pathMatch, navigate, params?.tab]);

  return (
    <div className={clsx('relative w-full', className)}>
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button
              data-testid="current-display-ws"
              className="min-w-f0 w-full"
              disabled={disabled}
            >
              <span
                className={clsx(
                  'flex items-center h-fit max-w-f16 font-normal transition-colors',
                  open ? 'text-main-500' : 'text-main hover:text-main-500'
                )}
              >
                <div className="truncate ">
                  {currentWorkspace?.name || 'Select a Workspace'}
                </div>
              </span>
            </Menu.Button>
            <Menu.Items
              className={clsx(
                'top-f2',
                'wideTablet:-left-f0',
                'border border-t-[0.5px] border-main-300',
                'bg-main-200 absolute -left-f0 max-h-f33 overflow-y-scroll z-20',
                'w-f19 min-w-f19 max-w-f19',
                'scrollbar-hide',
                'shadow-nice'
              )}
              data-testid="ws-dropdown"
            >
              {workspaces
                .filter((w) => w.id !== currentWorkspace?.id)
                .map(({ id, name }, idx) => (
                  <Menu.Item key={id} data-testid={id}>
                    {() => (
                      <WouterLink href={`~${buildNewWorkspaceUrl(id)}`}>
                        <a
                          title={name}
                          className={clsx(
                            'block w-full h-f2 p-f0',
                            'border-b-[0.5px] border-main-300 box-border',
                            'font-light truncate text-main',
                            'cursor-pointer hover:bg-negative'
                          )}
                        >
                          <span className="text-main-500">
                            {idx === workspaces.length - 2 ? '└╴' : '├╴'}
                          </span>
                          {name}
                        </a>
                      </WouterLink>
                    )}
                  </Menu.Item>
                ))}
              <Menu.Item data-testid="new-ws">
                {({ active }) => (
                  <WouterLink href="~/add-new-workspace">
                    <a
                      className={clsx(
                        'box-border',
                        'text-main-500 hover:text-main border-b-[0.5px] border-main-300',
                        active ? 'bg-canvas ' : '',
                        'hover:bg-negative transition-colors block w-full h-f2 font-normal truncate'
                      )}
                    >
                      New Workspace +
                    </a>
                  </WouterLink>
                )}
              </Menu.Item>
              <Menu.Item>
                <div className="h-f2" />
              </Menu.Item>
              <Menu.Item>
                <span className="block text-main-500 h-f2 truncate">
                  {userInfo?.email}
                </span>
              </Menu.Item>
              <Menu.Item>
                <LogoutButton />
              </Menu.Item>
            </Menu.Items>
          </>
        )}
      </Menu>
      {!!openSettingsTab && (
        <WorkspaceSettingsModal
          handleClose={handleClose}
          defaultTab={openSettingsTab}
        />
      )}
    </div>
  );
};
